import { Link } from 'react-router-dom'
import { TrendingUp, Clock, CheckCircle, XCircle, DollarSign, Activity, Loader2, RefreshCw, Lock, Wallet } from 'lucide-react'
import { useAdminStats } from '@/hooks/useAdminStats'

export function DashboardPage() {
  const { stats, isLoading, refetch } = useAdminStats()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-uni-500" />
      </div>
    )
  }

  const byStatus = stats?.byStatus || {}
  const contractStats = stats?.contractStats

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Panel de Control</h1>
        <button
          onClick={() => refetch()}
          className="p-2 rounded-lg hover:bg-dark-800 transition-colors text-dark-400 hover:text-white"
          title="Actualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Pendientes"
          value={byStatus.PENDING || 0}
          icon={Clock}
          color="text-yellow-400"
          bgColor="bg-yellow-500/20"
        />
        <StatCard
          title="Aprobados"
          value={byStatus.APPROVED || 0}
          icon={Activity}
          color="text-blue-400"
          bgColor="bg-blue-500/20"
        />
        <StatCard
          title="Completados"
          value={byStatus.COMPLETED || 0}
          icon={CheckCircle}
          color="text-accent-400"
          bgColor="bg-accent-500/20"
        />
        <StatCard
          title="Rechazados"
          value={byStatus.REJECTED || 0}
          icon={XCircle}
          color="text-red-400"
          bgColor="bg-red-500/20"
        />
      </div>

      {/* Volume Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-uni-600/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-uni-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">Volumen Total</p>
              <p className="text-lg font-semibold">
                {formatNumber(stats?.totalVolumeFormatted || '0')} USDT
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent-600/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-accent-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">Comisiones Totales</p>
              <p className="text-lg font-semibold">
                {formatNumber(stats?.totalFeesFormatted || '0')} USDT
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">Volumen 24h</p>
              <p className="text-lg font-semibold">
                {formatNumber(stats?.volume24hFormatted || '0')} USDT
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contract Stats */}
      {contractStats && (
        <div className="card p-4">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Lock className="w-4 h-4 text-uni-400" />
            Estado del Contrato
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-dark-400 text-sm">Locks Activos</p>
              <p className="text-lg font-mono">{formatNumber(contractStats.totalActiveLocks)}</p>
            </div>
            <div>
              <p className="text-dark-400 text-sm">Balance Contrato</p>
              <p className="text-lg font-mono">{formatNumber(contractStats.contractBalance)}</p>
            </div>
            <div>
              <p className="text-dark-400 text-sm">Total Procesado</p>
              <p className="text-lg font-mono">{formatNumber(contractStats.totalProcessed)}</p>
            </div>
            <div>
              <p className="text-dark-400 text-sm">Comisiones Contrato</p>
              <p className="text-lg font-mono">{formatNumber(contractStats.totalFeesCollected)}</p>
            </div>
          </div>
          {contractStats.isPaused && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-400 text-sm">
              El contrato está pausado
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="card p-4">
        <h2 className="font-semibold mb-3">Acciones Rápidas</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin/queue" className="btn-primary btn-sm">
            Ver Cola ({byStatus.PENDING || 0} pendientes)
          </Link>
          <Link to="/admin/config" className="btn-secondary btn-sm">
            Configuración
          </Link>
          <Link to="/admin/audit" className="btn-secondary btn-sm">
            Auditoría
          </Link>
          <Link to="/admin/holders" className="btn-secondary btn-sm">
            Holders
          </Link>
        </div>
      </div>

      {/* Summary */}
      <div className="card p-4">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <Wallet className="w-4 h-4 text-uni-400" />
          Resumen
        </h2>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-dark-400">Total solicitudes:</span>
              <span className="font-mono">{stats?.totalRequests || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-dark-400">Volumen 7 días:</span>
              <span className="font-mono">{formatNumber(stats?.volume7dFormatted || '0')} USDT</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-dark-400">Programados:</span>
              <span className="font-mono">{byStatus.SCHEDULED || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-dark-400">Cancelados:</span>
              <span className="font-mono">{byStatus.CANCELLED || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  icon: Icon,
  color,
  bgColor,
}: {
  title: string
  value: number | string
  icon: any
  color: string
  bgColor: string
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-dark-400 text-sm">{title}</p>
          <p className="text-2xl font-semibold mt-1">{value}</p>
        </div>
        <div className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </div>
  )
}

function formatNumber(value: string): string {
  const num = parseFloat(value)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
